import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ContentBox,
  ContentPage,
  NoResults,
  PageActions,
  Row,
  Select,
  Skeleton,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import ProjectHoursTab from "@components/ProjectHoursTab";
import HelpVideoButton from "@components/HelpVideoButton";
import { formatDate, usePageHeader } from "@components/ow";
import { CONTRACT_STATUS, remunerationSummary } from "../../Common/ContractDetail";

// Detalhe do projeto na visão do prestador: só as horas dele no projeto. O contrato
// define as regras do apontamento; com mais de um contrato no projeto, um select
// troca entre eles (abre no primeiro ativo).

const LIST_HREF = "/e/projects";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ContractField = styled.div`
  width: 480px;
`;

function contractOption(contract) {
  const status = contract.status !== "ACTIVE" && CONTRACT_STATUS[contract.status];
  const parts = [remunerationSummary(contract)];
  if (contract.start_date) parts.push(`Início ${formatDate(contract.start_date)}`);
  if (status) parts.push(status.charAt(0).toUpperCase() + status.slice(1));
  return { value: String(contract.id), label: parts.join(" · ") };
}

export default function ProjectDetailsEmployee() {
  const params = useParams();
  const navigate = useNavigate();
  const projectService = useService("projects");
  const contractService = useService("contracts");
  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");

  const [project, setProject] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const name = project?.name || "Projeto";
  usePageHeader({
    title: loading ? "Projeto" : name,
    breadcrumb: [
      { page: "Projetos", route: LIST_HREF },
      { page: loading ? "Projeto" : name, route: `${LIST_HREF}/${params.id}` },
    ],
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  async function fetchData() {
    setLoading(true);
    try {
      // O back filtra os contratos pelo prestador (role EMP)
      const [projectResult, contractsResult] = await Promise.all([
        projectService.get(params.id),
        contractService.list(`project_id=${params.id}`),
      ]);
      const list = contractsResult?.data?.list || [];
      const initial = list.find((c) => c.status === "ACTIVE") || list[0];
      setProject(projectResult.data);
      setContracts(list);
      setSelectedId(initial ? initial.id : null);
      setLoading(false);
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        toast.error("Você não tem acesso a este projeto");
        navigate(LIST_HREF);
        return;
      }
      toast.error("Erro ao carregar dados do projeto");
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <ContentPage aria-busy="true">
        <ContentBox>
          <Stack>
            <Row $justifyContent="space-between" $alignItems="flex-end">
              <Skeleton $width={220} $height={44} $noFlex />
              <Skeleton $width={160} $height={44} $noFlex />
            </Row>
            <Skeleton $height={56} />
            <Skeleton $height={320} />
          </Stack>
        </ContentBox>
      </ContentPage>
    );
  }

  const selected = contracts.find((c) => c.id === selectedId) || null;
  const options = contracts.map(contractOption);

  return (
    <ContentPage>
      {selected ? (
        <PageActions>
          <HelpVideoButton videoKey="employeeProjectHours" />
        </PageActions>
      ) : null}

      <ContentBox>
        {selected ? (
          <Stack>
            {contracts.length > 1 ? (
              <ContractField>
                <Select
                  $idForm="project-contract"
                  $label="Contrato"
                  $options={options}
                  value={options.find((o) => o.value === String(selected.id)) || null}
                  onChange={(option) => option && setSelectedId(parseInt(option.value))}
                />
              </ContractField>
            ) : null}
            <ProjectHoursTab
              key={selected.id}
              projectId={parseInt(params.id)}
              userId={user.id}
              isAdmin={false}
              hourMarkingModel={selected.hour_marking_model}
              hourTrackingMode={selected.hour_tracking_mode}
              contract={selected}
            />
          </Stack>
        ) : (
          <NoResults notResultText="Nenhum contrato seu neste projeto" />
        )}
      </ContentBox>
    </ContentPage>
  );
}
